/**
 * Despăgubiri eMAG — single page.
 *   Always-visible TOP SECTION: Total cards (paid / expected / rebuturi / net)
 *   Two tabs underneath: Matrice SKU  +  Cereri.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, RefreshCw, X, Upload, Trash2, Save, FileText, Package } from "lucide-react";
import { SkuAutocomplete } from "../components/SkuAutocomplete";
import ReceptionsTab from "./ReceptionsTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/compensations`;
const fmt = (v) => Number(v ?? 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ─────────────────────────────────────────────────────────────────────
// Always-visible TOP totals
// ─────────────────────────────────────────────────────────────────────
function TotalsHeader({ totals, rebuturi, onSync, syncing, onEditBaseline }) {
  if (!totals) return null;
  const netColor = totals.net > 0 ? "text-emerald-300" : totals.net < 0 ? "text-rose-300" : "text-zinc-300";
  return (
    <div className="space-y-3" data-testid="compensations-totals-header">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <button onClick={onEditBaseline} data-testid="comp-baseline-card"
          className="text-left rounded-xl border border-sky-500/40 bg-sky-500/5 p-3 hover:bg-sky-500/10 transition">
          <p className="text-[10px] uppercase tracking-wider text-sky-300/80">Valoare inițială</p>
          <p className="mt-1 font-mono text-xl font-bold text-sky-300">+{fmt(totals.baseline || 0)}</p>
          <p className="text-[10px] text-zinc-500 truncate" title={totals.baseline_note || "click pentru editare"}>
            {totals.baseline_note || "istoric — click pentru editare"}
          </p>
        </button>
        <div className="rounded-xl border border-zinc-800 bg-zinc-950/40 p-3">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">Despăgubiri primite (cereri)</p>
          <p className="mt-1 font-mono text-xl font-bold text-emerald-300">+{fmt(totals.paid_total)}</p>
          <p className="text-[10px] text-zinc-500">{totals.claims_finalized} cereri · {totals.claims_open} deschise</p>
        </div>
        <div className="rounded-xl border border-zinc-800 bg-zinc-950/40 p-3">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">Așteptat (finalizate)</p>
          <p className="mt-1 font-mono text-xl font-bold text-zinc-200">{fmt(totals.expected_total)}</p>
          <p className={`text-[10px] font-bold ${totals.diff_finalized < 0 ? "text-rose-300" : "text-emerald-300"}`}>
            Diff: {totals.diff_finalized > 0 ? "+" : ""}{fmt(totals.diff_finalized)}
          </p>
        </div>
        <div className="rounded-xl border border-zinc-800 bg-zinc-950/40 p-3">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">Cost Rebuturi (Oblio)</p>
          <p className="mt-1 font-mono text-xl font-bold text-rose-300">−{fmt(totals.rebuturi_cost)}</p>
          <p className="text-[10px] text-zinc-500">{rebuturi?.items?.length || 0} produse</p>
        </div>
        <div className="rounded-xl border-2 border-orange-500/40 bg-orange-500/5 p-3" data-testid="comp-net-card">
          <p className="text-[10px] uppercase tracking-wider text-zinc-400">NET RON</p>
          <p className={`mt-1 font-mono text-2xl font-bold ${netColor}`}>{totals.net >= 0 ? "+" : ""}{fmt(totals.net)}</p>
          <p className="text-[10px] text-zinc-500">(inițial + cereri) − rebuturi</p>
        </div>
      </div>
      <div className="flex items-center justify-between rounded-lg border border-zinc-800 bg-zinc-950/40 px-3 py-2">
        <p className="text-[11px] text-zinc-400">
          <Package className="mr-1 inline h-3 w-3" />
          Rebuturi sync: {totals.rebuturi_synced_at ? new Date(totals.rebuturi_synced_at).toLocaleString("ro-RO") : <span className="text-zinc-600">niciodată</span>}
        </p>
        <button onClick={onSync} disabled={syncing} data-testid="rebuturi-sync"
          className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-700 bg-zinc-900 px-2.5 py-1 text-[11px] text-zinc-200 disabled:opacity-50">
          <RefreshCw className={`h-3 w-3 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Sync..." : "Sync Rebuturi"}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Tab 1: Matrice (only RO names)
// ─────────────────────────────────────────────────────────────────────
function MatrixTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("active"); // active | inactive | all
  const [edits, setEdits] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== "all" ? { status: statusFilter } : {};
      setItems((await axios.get(`${API}/matrix`, { params })).data?.items || []);
    }
    catch (e) { toast.error("Eroare: " + (e.response?.data?.detail || e.message)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line

  const save = async (sku) => {
    const v = edits[sku];
    try {
      await axios.put(`${API}/matrix/${sku}`, { compensation_amount: v === "" ? null : Number(v) });
      toast.success(`${sku} salvat`);
      setEdits((p) => { const x = { ...p }; delete x[sku]; return x; });
      load();
    } catch (e) { toast.error("Eroare: " + (e.response?.data?.detail || e.message)); }
  };

  const filtered = items.filter(i => !search || i.sku.toLowerCase().includes(search.toLowerCase()) || (i.name || "").toLowerCase().includes(search.toLowerCase()));
  const filled = items.filter(i => i.compensation_amount != null).length;

  const FILTERS = [
    { v: "active", l: "Activate", cls: "bg-emerald-500/20 text-emerald-200" },
    { v: "inactive", l: "Dezactivate", cls: "bg-rose-500/20 text-rose-200" },
    { v: "all", l: "Toate", cls: "bg-zinc-700 text-zinc-100" },
  ];

  return (
    <div className="space-y-3" data-testid="compensations-matrix-tab">
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-lg border border-zinc-800 bg-zinc-950 p-0.5" data-testid="matrix-status-filter">
          {FILTERS.map(f => (
            <button key={f.v} onClick={() => setStatusFilter(f.v)}
              data-testid={`matrix-filter-${f.v}`}
              className={`px-3 py-1 text-[11px] font-medium rounded-md transition ${
                statusFilter === f.v ? f.cls : "text-zinc-400 hover:text-zinc-200"
              }`}>
              {f.l}
            </button>
          ))}
        </div>
        <input value={search} onChange={(e) => setSearch(e.target.value)}
          data-testid="comp-matrix-search"
          placeholder="Caută SKU sau nume..."
          className="flex-1 min-w-[200px] rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-orange-500/50" />
        <span className="text-[11px] text-zinc-500">{filled}/{items.length} completate</span>
      </div>
      {loading ? <p className="text-xs text-zinc-500">Se încarcă...</p> : (
        <div className="overflow-x-auto rounded-lg border border-zinc-800">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900 text-[11px] uppercase text-zinc-400">
              <tr>
                <th className="w-12 px-3 py-2 text-left">#</th>
                <th className="px-3 py-2 text-left">SKU</th>
                <th className="px-3 py-2 text-left">Nume (RO)</th>
                <th className="w-24 px-3 py-2 text-center">Status</th>
                <th className="w-44 px-3 py-2 text-right">Despăgubire (RON cu TVA)</th>
                <th className="w-24 px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it, i) => {
                const editing = edits[it.sku] !== undefined;
                const display = editing ? edits[it.sku] : (it.compensation_amount ?? "");
                return (
                  <tr key={it.sku} className="border-t border-zinc-900" data-testid={`comp-matrix-row-${it.sku}`}>
                    <td className="px-3 py-2 text-zinc-500">{i + 1}</td>
                    <td className="px-3 py-2 font-mono text-sky-300">{it.sku}</td>
                    <td className="px-3 py-2 text-zinc-200" title={it.name}>{(it.name || "").slice(0, 80)}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ${it.is_active ? "bg-emerald-500/15 text-emerald-200" : "bg-zinc-800 text-zinc-400"}`}>
                        {it.is_active ? "Activ" : "Inactiv"}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <input type="number" step="0.01" value={display}
                        onChange={(e) => setEdits(p => ({ ...p, [it.sku]: e.target.value }))}
                        data-testid={`comp-matrix-input-${it.sku}`}
                        className={`w-full rounded border px-2 py-1 text-right text-sm outline-none ${editing ? "border-orange-500/50 bg-zinc-900" : "border-zinc-800 bg-zinc-950"} ${it.compensation_amount == null && !editing ? "text-zinc-600" : "text-zinc-100"}`}
                        placeholder="—" />
                    </td>
                    <td className="px-3 py-2 text-right">
                      {editing && (
                        <button onClick={() => save(it.sku)} data-testid={`comp-matrix-save-${it.sku}`}
                          className="inline-flex items-center gap-1 rounded bg-emerald-500/15 px-2 py-1 text-[11px] text-emerald-200 hover:bg-emerald-500/25">
                          <Save className="h-3 w-3" /> Salvează
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Product autocomplete (used inside claim editor)
// ─────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────
// Tab 2: Cereri
// ─────────────────────────────────────────────────────────────────────
function ClaimsTab({ onChanged }) {
  const [claims, setClaims] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, m, pc] = await Promise.all([
        axios.get(`${API}/claims`),
        axios.get(`${API}/matrix`),
        // Merge in the full product catalog so the SKU dropdown always
        // shows every product, not only those already in the compensation
        // matrix. When a SKU exists in both lists we keep the
        // `compensation_amount` from the matrix side.
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/products/catalog`)
          .catch(() => ({ data: { items: [] } })),
      ]);
      setClaims(c.data?.items || []);
      const matrix = m.data?.items || [];
      const matrixBySku = Object.fromEntries(matrix.map((it) => [it.sku, it]));
      const catalogItems = pc.data?.items || [];
      const merged = [];
      const seen = new Set();
      for (const cat of catalogItems) {
        const mx = matrixBySku[cat.sku];
        merged.push({
          sku: cat.sku,
          name: cat.name || mx?.name || "",
          compensation_amount: mx?.compensation_amount ?? null,
        });
        seen.add(cat.sku);
      }
      // Matrix entries that are NOT in the products catalog (legacy / archived
      // SKUs the user still wants to raise claims for).
      for (const mx of matrix) {
        if (!seen.has(mx.sku)) merged.push(mx);
      }
      merged.sort((a, b) => a.sku.localeCompare(b.sku));
      setCatalog(merged);
    } catch (e) { toast.error("Eroare: " + (e.response?.data?.detail || e.message)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Sigur ștergi această cerere?")) return;
    try { await axios.delete(`${API}/claims/${id}`); toast.success("Șters"); load(); onChanged?.(); }
    catch (e) { toast.error("Eroare: " + (e.response?.data?.detail || e.message)); }
  };

  return (
    <div className="space-y-4" data-testid="compensations-claims-tab">
      <div className="flex items-center justify-between">
        <p className="text-xs text-zinc-500">{claims.length} cereri ({claims.filter(c => c.status === "finalized").length} finalizate)</p>
        <button onClick={() => setCreating(true)} data-testid="comp-claim-new"
          className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-b from-orange-500 to-rose-500 px-3 py-1.5 text-xs font-medium text-white">
          <Plus className="h-3.5 w-3.5" /> Cerere nouă
        </button>
      </div>
      {loading ? <p className="text-xs text-zinc-500">Se încarcă...</p> : claims.length === 0 ? (
        <p className="rounded-lg border border-dashed border-zinc-800 px-4 py-6 text-center text-xs text-zinc-500">Nicio cerere încă.</p>
      ) : (
        <div className="space-y-2">
          {claims.map((c) => (
            <div key={c.id} className="rounded-lg border border-zinc-800 bg-zinc-950/40 p-3" data-testid={`comp-claim-${c.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-zinc-100">Cerere <span className="font-mono text-sky-300">{c.claim_number || "(fără număr)"}</span></p>
                  <p className="text-[11px] text-zinc-500" data-testid={`comp-claim-${c.id}-counts`}>
                    SKU-uri: <span className="text-zinc-300">{c.items?.length || 0}</span>
                    {" · "}
                    Buc: <span className="text-zinc-300">{(c.items || []).reduce((s, it) => s + (parseInt(it.qty) || 0), 0)}</span>
                    {" · "}
                    {new Date(c.created_at).toLocaleDateString("ro-RO")}
                  </p>
                </div>
                <div className="flex items-center gap-3 text-right">
                  <div className="text-[11px]">
                    <p className="text-zinc-400">Așteptat: <span className="font-mono text-zinc-100">{fmt(c.expected_total)} RON</span></p>
                    {c.paid_total != null && (
                      <p className="text-zinc-400">Plătit: <span className="font-mono text-emerald-300">{fmt(c.paid_total)} RON</span></p>
                    )}
                    {c.diff != null && (
                      <p className={`font-mono font-bold ${c.diff < 0 ? "text-rose-300" : c.diff > 0 ? "text-emerald-300" : "text-zinc-400"}`}>
                        Diff: {c.diff > 0 ? "+" : ""}{fmt(c.diff)}
                      </p>
                    )}
                  </div>
                  {(() => {
                    const isFinalized = c.status === "finalized";
                    const isPartial = isFinalized && c.diff != null && c.diff < 0;
                    const label = isPartial ? "Finalizat Parțial" : (isFinalized ? "Finalizat" : "Deschis");
                    const cls = isPartial
                      ? "bg-amber-500/15 text-amber-200"
                      : isFinalized
                      ? "bg-emerald-500/15 text-emerald-200"
                      : "bg-amber-500/15 text-amber-200";
                    return (
                      <span className={`rounded px-2 py-0.5 text-[10px] font-semibold ${cls}`} data-testid={`comp-claim-${c.id}-status`}>
                        {label}
                      </span>
                    );
                  })()}
                  <button onClick={() => setEditing(c)} data-testid={`comp-claim-edit-${c.id}`} className="rounded border border-zinc-700 bg-zinc-900 px-2 py-1 text-[11px] text-zinc-200 hover:text-zinc-100">Detalii</button>
                  <button onClick={() => del(c.id)} className="rounded border border-rose-500/40 bg-rose-500/10 p-1 text-rose-200">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {creating && <ClaimEditor catalog={catalog} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); onChanged?.(); }} />}
      {editing && <ClaimEditor catalog={catalog} claim={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); onChanged?.(); }} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Claim editor (create + edit)
// ─────────────────────────────────────────────────────────────────────
function ClaimEditor({ claim, catalog, onClose, onSaved }) {
  const isNew = !claim;
  const [claimNumber, setClaimNumber] = useState(claim?.claim_number || "");
  const [items, setItems] = useState(claim?.items || []);
  const [paidTotal, setPaidTotal] = useState(claim?.paid_total ?? "");
  const [notes, setNotes] = useState(claim?.notes || "");
  const [saving, setSaving] = useState(false);
  const [parsing, setParsing] = useState(false);

  // Live recompute expected per row using catalog (matrix amounts)
  const catalogMap = useMemo(() => Object.fromEntries(catalog.map(c => [c.sku.toUpperCase(), c])), [catalog]);
  const enrichedItems = items.map(it => {
    const sku = (it.sku || "").toUpperCase();
    const match = catalogMap[sku];
    const perUnit = it.expected_amount_per_unit != null ? it.expected_amount_per_unit : (match?.compensation_amount ?? null);
    const subtotal = perUnit != null ? Number((perUnit * (it.qty || 0)).toFixed(2)) : null;
    return { ...it, expected_amount_per_unit: perUnit, expected_subtotal: subtotal, _name: match?.name };
  });
  const expectedTotal = enrichedItems.reduce((s, it) => s + (it.expected_subtotal || 0), 0);

  const setRow = (i, patch) => setItems((p) => p.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const delRow = (i) => setItems((p) => p.filter((_, idx) => idx !== i));
  const addRow = () => setItems((p) => [...p, { sku: "", qty: 1, expected_amount_per_unit: null }]);

  const parseDoc = async (file) => {
    setParsing(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const ext = (file.name || "").toLowerCase();
      const url = ext.endsWith(".xlsx") || ext.endsWith(".xls") ? `${API}/parse-claim-excel` : `${API}/parse-claim-doc`;
      const r = await axios.post(url, fd);
      if (!r.data?.ok) { toast.error(r.data?.error || "Parsing eșuat"); return; }
      if (r.data.claim_number) setClaimNumber(r.data.claim_number);
      const parsed = (r.data.items || []).map(it => ({ sku: it.sku, qty: it.qty, expected_amount_per_unit: null }));
      setItems((p) => [...p, ...parsed]);
      toast.success(`Adăugate ${parsed.length} produse`);
    } catch (e) { toast.error("Parsing: " + (e.response?.data?.detail || e.message)); }
    finally { setParsing(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        claim_number: claimNumber,
        items: items.filter(it => (it.sku || "").trim() && it.qty > 0),
        paid_total: paidTotal === "" ? null : Number(paidTotal),
        notes,
      };
      if (isNew) await axios.post(`${API}/claims`, payload);
      else await axios.put(`${API}/claims/${claim.id}`, payload);
      toast.success("Salvat");
      onSaved();
    } catch (e) { toast.error("Eroare: " + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose} data-testid="claim-editor">
      <div className="w-full max-w-3xl rounded-xl border border-zinc-800 bg-zinc-950 p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-zinc-100">{isNew ? "Cerere nouă de despăgubire" : `Cerere ${claim.claim_number || ""}`}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-100"><X className="h-4 w-4" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">Număr cerere eMAG</label>
            <input value={claimNumber} onChange={(e) => setClaimNumber(e.target.value)} data-testid="claim-number-input"
              className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-orange-500/50" />
          </div>
          <div>
            <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">Sumă plătită de eMAG (după ce primești)</label>
            <input type="number" step="0.01" value={paidTotal} onChange={(e) => setPaidTotal(e.target.value)} data-testid="claim-paid-input"
              placeholder="Lasă gol până nu primești plata"
              className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-emerald-500/50" />
          </div>
        </div>
        <div className="mb-3 flex items-center justify-between">
          <h4 className="text-xs font-semibold uppercase text-zinc-300">Produse ({items.length})</h4>
          <div className="flex gap-2">
            <label className="inline-flex cursor-pointer items-center gap-1 rounded-lg border border-zinc-700 bg-zinc-900 px-2.5 py-1 text-[11px] text-zinc-200 hover:text-zinc-100">
              <Upload className="h-3 w-3" /> {parsing ? "Se parsează..." : "Upload PDF/IMG/XLSX"}
              <input type="file" accept=".pdf,.xlsx,.xls,image/*" onChange={(e) => e.target.files?.[0] && parseDoc(e.target.files[0])} className="hidden" data-testid="claim-upload" />
            </label>
            <button onClick={addRow} data-testid="claim-add-row"
              className="inline-flex items-center gap-1 rounded-lg border border-zinc-700 bg-zinc-900 px-2.5 py-1 text-[11px] text-zinc-200">
              <Plus className="h-3 w-3" /> Adaugă produs
            </button>
          </div>
        </div>
        <div className="max-h-96 overflow-y-auto rounded-lg border border-zinc-800">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900 text-[10px] uppercase text-zinc-400">
              <tr>
                <th className="px-2 py-1.5 text-left">SKU / Nume</th>
                <th className="w-20 px-2 py-1.5 text-right">Buc</th>
                <th className="w-32 px-2 py-1.5 text-right">Unitar matrice</th>
                <th className="w-32 px-2 py-1.5 text-right">Subtotal</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {enrichedItems.map((it, i) => (
                <tr key={i} className="border-t border-zinc-900">
                  <td className="px-2 py-1">
                    <SkuAutocomplete value={it.sku} catalog={catalog}
                      testId={`claim-row-${i}-sku`}
                      onPick={(m) => setRow(i, { sku: m.sku, expected_amount_per_unit: m.compensation_amount ?? null })} />
                    {it._name && <p className="mt-0.5 truncate text-[10px] text-zinc-500" title={it._name}>{it._name.slice(0, 60)}</p>}
                  </td>
                  <td className="px-2 py-1">
                    <input type="number" step="1" min="1" value={it.qty || ""} onChange={(e) => setRow(i, { qty: parseInt(e.target.value) || 0 })}
                      data-testid={`claim-row-${i}-qty`}
                      className="w-full rounded border border-zinc-800 bg-zinc-900 px-2 py-1 text-right text-zinc-100 outline-none" />
                  </td>
                  <td className="px-2 py-1 text-right text-[11px] text-zinc-400">{it.expected_amount_per_unit != null ? fmt(it.expected_amount_per_unit) : <span className="text-zinc-600 italic">— neîncadrat</span>}</td>
                  <td className="px-2 py-1 text-right font-mono text-emerald-300">{it.expected_subtotal != null ? fmt(it.expected_subtotal) : "—"}</td>
                  <td className="px-2 py-1 text-right">
                    <button onClick={() => delRow(i)} className="rounded p-1 text-rose-300 hover:bg-rose-500/10"><X className="h-3 w-3" /></button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={5} className="px-2 py-6 text-center text-xs text-zinc-500">Adaugă produse manual sau încarcă PDF/imagine/Excel.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="mt-3 flex items-center justify-between rounded-lg bg-zinc-900/60 px-3 py-2 text-xs">
          <span className="text-zinc-400">Total așteptat:</span>
          <span className="font-mono text-base font-bold text-emerald-300" data-testid="claim-expected-total">{fmt(expectedTotal)} RON</span>
        </div>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Note (opțional)"
          className="mt-3 w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none" rows="2" />
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300">Anulează</button>
          <button onClick={save} disabled={saving} data-testid="claim-save"
            className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-b from-orange-500 to-rose-500 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50">
            <Save className="h-3.5 w-3.5" /> {saving ? "..." : "Salvează cererea"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Page shell
// ─────────────────────────────────────────────────────────────────────
const TABS = [
  { id: "matrix", label: "Matrice SKU" },
  { id: "claims", label: "Cereri Despăgubiri" },
  { id: "receptions", label: "Cereri Recepții" },
];

export default function Compensations() {
  const [tab, setTab] = useState("matrix");
  const [totals, setTotals] = useState(null);
  const [rebuturi, setRebuturi] = useState({ items: [] });
  const [syncing, setSyncing] = useState(false);

  const loadTotals = async () => {
    try {
      const [t, r] = await Promise.all([
        axios.get(`${API}/totals`),
        axios.get(`${API}/oblio-rebuturi`),
      ]);
      setTotals(t.data); setRebuturi(r.data);
    } catch (e) { toast.error("Eroare totals: " + (e.response?.data?.detail || e.message)); }
  };
  useEffect(() => { loadTotals(); }, []);

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/oblio-rebuturi/sync`);
      if (r.data?.ok) toast.success(`Sync OK: ${r.data.items} produse · ${fmt(r.data.total_cost)} RON`);
      else toast.error(r.data?.error || "Sync eșuat");
      loadTotals();
    } catch (e) { toast.error("Eroare: " + (e.response?.data?.detail || e.message)); }
    finally { setSyncing(false); }
  };

  const editBaseline = async () => {
    const cur = totals?.baseline || 0;
    const val = window.prompt(
      "Valoarea inițială (RON) — total despăgubiri deja primite de la eMAG înainte de tracking:",
      String(cur)
    );
    if (val === null) return;
    const amount = parseFloat(String(val).replace(",", "."));
    if (!Number.isFinite(amount) || amount < 0) { toast.error("Valoare invalidă"); return; }
    const note = window.prompt(
      "Notă (opțional) — ex. Despăgubiri primite până la data de ...",
      totals?.baseline_note || ""
    );
    try {
      await axios.put(`${API}/baseline`, { amount, note: note || null });
      toast.success(`Baseline setat: +${fmt(amount)} RON`);
      loadTotals();
    } catch (e) { toast.error("Eroare: " + (e.response?.data?.detail || e.message)); }
  };

  return (
    <div className="space-y-4 p-5" data-testid="compensations-page">
      <header>
        <h1 className="text-2xl font-bold text-zinc-100"><FileText className="mr-2 inline h-6 w-6" /> Cereri Despăgubiri și Recepții eMAG</h1>
        <p className="mt-1 text-xs text-zinc-500">Despăgubiri eMAG (FBE RO) − cost rebuturi Oblio = diferență netă</p>
      </header>

      {/* Totals always visible at top */}
      <TotalsHeader totals={totals} rebuturi={rebuturi} onSync={sync} syncing={syncing} onEditBaseline={editBaseline} />

      {/* Tabs */}
      <nav className="flex gap-1 border-b border-zinc-800">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            data-testid={`comp-tab-${t.id}`}
            className={`relative px-4 py-2 text-sm transition ${tab === t.id ? "text-orange-300" : "text-zinc-400 hover:text-zinc-200"}`}>
            {t.label}
            {tab === t.id && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-orange-500 to-rose-500" />}
          </button>
        ))}
      </nav>
      {tab === "matrix" && <MatrixTab />}
      {tab === "claims" && <ClaimsTab onChanged={loadTotals} />}
      {tab === "receptions" && (
        <ReceptionsTab />
      )}
    </div>
  );
}
